import React, { memo } from 'react';

const keyframeCSS = `
  @media (max-width: 768px) {
    .box-stage {
      padding: 2px !important;
    }
    .box-stage-floor {
      padding: 6px 4px 10px !important;
    }
    .box-grid {
      gap: 4px !important;
      max-width: 100% !important;
      grid-template-columns: repeat(4, 1fr) !important;
    }
    .box-region-label {
      font-size: 0.42rem !important;
    }
    .box-number-plate {
      font-size: 0.65rem !important;
      padding: 1px 5px !important;
    }
    .box-prize-value {
      font-size: 0.42rem !important;
    }
  }
  @media (max-width: 400px) {
    .box-grid {
      gap: 3px !important;
    }
  }
  @keyframes boxShine {
    0% { left: -100%; }
    50%, 100% { left: 200%; }
  }
  @keyframes playerGlow {
    0%, 100% { box-shadow: 0 0 15px rgba(212,175,55,0.4), 0 0 30px rgba(212,175,55,0.2); }
    50% { box-shadow: 0 0 25px rgba(212,175,55,0.7), 0 0 50px rgba(212,175,55,0.3), 0 0 80px rgba(212,175,55,0.15); }
  }
  @keyframes boxFloat {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-5px); }
  }
  @keyframes lidOpenAnim {
    0% { transform: rotateX(0deg); }
    100% { transform: rotateX(-110deg); }
  }
  @keyframes innerGlowPulse {
    0%, 100% { opacity: 0.4; }
    50% { opacity: 0.8; }
  }
  @keyframes prizeFloat {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-3px); }
  }
`;

function formatEuro(value) {
  if (typeof value !== 'number') return String(value);
  return value.toLocaleString('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: value < 1 ? 2 : 0,
    maximumFractionDigits: 2,
  });
}

export default function BoxGrid({ boxes = [], playerBoxNumber, onBoxClick, disabled = false, selectionMode = false, showAllPrizes = false }) {
  const sorted = [...boxes].sort((a, b) => a.box_number - b.box_number);

  const stageStyle = {
    width: '100%',
    maxWidth: '520px',
    margin: '0 auto',
    padding: '4px',
  };

  const stageFloorStyle = {
    background: 'linear-gradient(180deg, transparent 0%, rgba(212,175,55,0.03) 60%, rgba(212,175,55,0.06) 100%)',
    borderRadius: '12px',
    padding: '16px 12px 20px',
    position: 'relative',
    overflow: 'hidden',
  };

  const stageReflectionStyle = {
    position: 'absolute',
    bottom: 0,
    left: '10%',
    right: '10%',
    height: '1px',
    background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.2), transparent)',
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    gap: '8px',
    maxWidth: '520px',
    margin: '0 auto',
  };

  const selectionHintStyle = {
    textAlign: 'center',
    fontFamily: 'var(--font-display)',
    fontSize: '1.1rem',
    fontWeight: 600,
    color: '#D4AF37',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    marginBottom: '16px',
    textShadow: '0 0 20px rgba(212,175,55,0.3)',
  };

  return (
    <div className="box-stage" style={stageStyle}>
      <style>{keyframeCSS}</style>

      {selectionMode && (
        <div style={selectionHintStyle}>
          Seleziona il tuo pacco
        </div>
      )}

      <div className="box-stage-floor" style={stageFloorStyle}>
        <div style={stageReflectionStyle} />
        <div className="box-grid" style={gridStyle}>
          {sorted.map((box, index) => {
            const isPlayerBox = box.is_player_box || box.box_number === playerBoxNumber;
            const clickable = selectionMode
              ? !box.is_opened
              : !box.is_opened && !isPlayerBox && !disabled;

            return (
              <BoxItem
                key={box.box_number}
                box={box}
                index={index}
                isPlayerBox={isPlayerBox}
                clickable={clickable}
                selectionMode={selectionMode}
                onBoxClick={onBoxClick}
                showPrize={showAllPrizes}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

const BoxItem = memo(function BoxItem({ box, index, isPlayerBox, clickable, selectionMode, onBoxClick, showPrize }) {
  const isOpened = box.is_opened;

  // Box wrapper - contains 3D perspective
  const wrapperStyle = {
    perspective: '600px',
    cursor: clickable ? 'pointer' : 'default',
    position: 'relative',
  };

  // Main 3D box container
  const box3dStyle = {
    position: 'relative',
    width: '100%',
    aspectRatio: '3 / 3.5',
    transformStyle: 'preserve-3d',
  };

  // Box body - blue/celeste like RAI game show
  const bodyGradient = isOpened
    ? 'linear-gradient(180deg, #1a3a5c 0%, #152d4a 50%, #0f2038 100%)'
    : 'linear-gradient(180deg, #2196F3 0%, #1976D2 40%, #1565C0 100%)';

  const bodyStyle = {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '78%',
    background: bodyGradient,
    borderRadius: '4px 4px 6px 6px',
    border: isPlayerBox && !isOpened ? '2px solid #D4AF37' : '1px solid rgba(33,150,243,0.5)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '3px',
    overflow: 'hidden',
    boxShadow: isOpened
      ? 'inset 0 2px 8px rgba(0,0,0,0.5)'
      : '0 4px 12px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1)',
    opacity: isOpened ? 0.5 : 1,
    animation: isPlayerBox && !isOpened ? 'playerGlow 2s ease-in-out infinite' : 'none',
  };

  // Lid
  const lidStyle = {
    position: 'absolute',
    top: 0,
    left: '-2px',
    right: '-2px',
    height: '28%',
    background: isOpened
      ? 'linear-gradient(180deg, #1a3a5c 0%, #152d4a 100%)'
      : 'linear-gradient(180deg, #42A5F5 0%, #2196F3 100%)',
    borderRadius: '4px 4px 2px 2px',
    border: isPlayerBox && !isOpened ? '2px solid #D4AF37' : '1px solid rgba(66,165,245,0.6)',
    transformOrigin: 'top center',
    animation: isOpened ? 'lidOpenAnim 0.6s ease-out forwards' : 'none',
    boxShadow: isOpened
      ? 'none'
      : '0 2px 6px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.15)',
    zIndex: 2,
    opacity: isOpened ? 0.4 : 1,
  };

  // Gold number plate
  const numberPlateStyle = {
    background: 'linear-gradient(135deg, #D4AF37 0%, #F0D060 50%, #D4AF37 100%)',
    color: '#2a1a00',
    fontFamily: 'var(--font-display)',
    fontSize: 'clamp(0.75rem, 1.5vw, 1rem)',
    fontWeight: 700,
    padding: '2px 10px',
    borderRadius: '3px',
    textAlign: 'center',
    lineHeight: 1.3,
    boxShadow: '0 2px 4px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.3)',
    position: 'relative',
    zIndex: 3,
    minWidth: '32px',
  };

  // Region label
  const regionStyle = {
    fontSize: '0.6rem',
    fontFamily: 'var(--font-display)',
    fontWeight: 600,
    color: 'rgba(255,255,255,0.9)',
    textAlign: 'center',
    lineHeight: 1.2,
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
    padding: '1px 4px',
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    zIndex: 3,
    position: 'relative',
    textShadow: '0 1px 3px rgba(0,0,0,0.5)',
  };

  // Shine overlay
  const shineStyle = {
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '50%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)',
    animation: !isOpened ? 'boxShine 4s ease-in-out infinite' : 'none',
    animationDelay: `${index * 0.2}s`,
    zIndex: 4,
    pointerEvents: 'none',
  };

  // Prize reveal inside opened box
  const prizeVal = box.prize_value;
  const isLongPrize = prizeVal !== undefined && prizeVal >= 10000;
  const prizeRevealStyle = {
    position: 'absolute',
    bottom: '10%',
    left: '2px',
    right: '2px',
    fontFamily: 'var(--font-mono)',
    fontSize: isLongPrize ? '0.6rem' : '0.7rem',
    fontWeight: 700,
    color: '#F0D060',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    zIndex: 5,
    textShadow: '0 0 10px rgba(212,175,55,0.6), 0 0 20px rgba(212,175,55,0.3)',
    animation: 'prizeFloat 3s ease-in-out infinite',
    lineHeight: 1,
  };

  // Inner glow for opened box
  const innerGlowStyle = {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '78%',
    background: 'radial-gradient(ellipse at center, rgba(212,175,55,0.15) 0%, transparent 70%)',
    animation: 'innerGlowPulse 2s ease-in-out infinite',
    pointerEvents: 'none',
    zIndex: 1,
    borderRadius: '4px',
  };

  // Player glow ring
  const playerGlowStyle = {
    position: 'absolute',
    inset: '-4px',
    borderRadius: '8px',
    background: 'transparent',
    border: '2px solid rgba(212,175,55,0.3)',
    animation: 'playerGlow 2s ease-in-out infinite',
    pointerEvents: 'none',
    zIndex: 0,
  };

  const interactiveStyle = clickable ? {
    cursor: 'pointer',
    transition: 'transform 0.15s ease',
  } : {};

  return (
    <div
      style={{ ...wrapperStyle, ...interactiveStyle }}
      onClick={() => clickable && onBoxClick?.(box.box_number)}
      onMouseEnter={clickable ? (e) => { e.currentTarget.style.transform = 'scale(1.08) translateY(-6px)'; } : undefined}
      onMouseLeave={clickable ? (e) => { e.currentTarget.style.transform = ''; } : undefined}
    >
      <div style={box3dStyle}>
        {/* Lid */}
        <div style={lidStyle} />

        {/* Box body */}
        <div style={bodyStyle}>
          {/* Number plate */}
          {!isOpened && (
            <div className="box-number-plate" style={numberPlateStyle}>
              {box.box_number}
            </div>
          )}

          {/* Region label */}
          {!isOpened && box.region_name && (
            <div className="box-region-label" style={{
              ...regionStyle,
              ...(showPrize ? { fontSize: '0.45rem', padding: 0 } : {}),
            }}>{box.region_name}</div>
          )}

          {/* Prize value visible to organizer */}
          {!isOpened && showPrize && box.prize_value !== undefined && (
            <div className="box-prize-value" style={{
              fontSize: '0.5rem',
              fontFamily: 'var(--font-mono)',
              fontWeight: 700,
              color: '#F0D060',
              textAlign: 'center',
              lineHeight: 1,
              textShadow: '0 1px 3px rgba(0,0,0,0.5)',
              zIndex: 3,
              position: 'relative',
            }}>
              {box.prize_label && (box.prize_value === 0 || box.prize_value === undefined)
                ? box.prize_label
                : formatEuro(box.prize_value)}
            </div>
          )}

          {/* Shine effect */}
          {!isOpened && <div style={shineStyle} />}
        </div>

        {/* Prize reveal for opened boxes */}
        {isOpened && (box.prize_value !== undefined || box.prize_label) && (
          <div className="box-prize-value" style={prizeRevealStyle}>
            {box.prize_label && (box.prize_value === 0 || box.prize_value === undefined)
              ? box.prize_label
              : formatEuro(box.prize_value)}
          </div>
        )}

        {/* Inner glow for opened boxes */}
        {isOpened && <div style={innerGlowStyle} />}

        {/* Player box special glow */}
        {isPlayerBox && !isOpened && <div style={playerGlowStyle} />}
      </div>
    </div>
  );
});
